/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

// Lib
import { apiClient } from '@lib/api-client';
import {
	queryClient,
	TMutationConfig,
} from '@lib/react-query';

import { queryKeys } from '@config/constants/query-keys';
import { TApiError } from '@global-types/api/api';
import {
	TApiResponseCreateUser,
	TBodyCreateUser,
} from '@features/resources/producers/types';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const addUser = (
	body: TBodyCreateUser
): Promise<TApiResponseCreateUser> => {
	return apiClient.post('/users', body);
};

/* ---------------------------------------------------------------------------- */
/*                                     Type                                    */
/* -------------------------------------------------------------------------- */

type TUseAddUserOptions = {
	mutationConfig?: TMutationConfig<typeof addUser>;
	page: number;
	size: number;
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useAddUser = ({
	page,
	size,
	mutationConfig,
}: TUseAddUserOptions) => {
	const { t } = useTranslation();
	const { onSuccess, onError } = mutationConfig || {};
	const {
		mutate,
		isPending: isLoading,
		isSuccess,
	} = useMutation({
		mutationFn: addUser,
		onSuccess: async (...args) => {
			onSuccess?.(...args);
			notifications.show({
				title: t('global.success'),
				message: t(
					'users.notifications.userSuccessfullyAdded'
				),
				color: 'success',
			});

			queryClient.invalidateQueries({
				queryKey: [
					queryKeys.app.users.list.key,
					{ page: page, size: size },
				],
			});
		},
		onError: (error: TApiError, variables, context) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'error',
			});
			onError?.(error, variables, context);
		},
	});

	return { mutate, isLoading, isSuccess };
};
